import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { INTEGRATION_TYPES } from 'shared/constants/integrations';
import { getOVSAffiliateSelector } from 'store/onlineVisit/onlineVisit.redux';
import { getUserSelector } from 'store/user/user.redux';
import { show } from '@intercom/messenger-js-sdk';
import { IUser } from 'shared/interfaces/user.interface';
import { IIntegrationConfiguration } from 'shared/interfaces/affiliate.interface';

export const INTERCOM_ACTIONS = {
  OPEN_CHAT: 'open_chat'
};

export const ZOHO_DESK_ACTIONS = {
  GET_WIDGET_IFRAME: 'get_widget_iframe'
};

export interface IUseIntegrations {
  enabledIntegrations: any[];
  getIntegration: (integrationKey: string) => IIntegrationConfiguration;
  integrationAccessLevel: {
    authenticated: boolean;
    user: IUser | null;
  };
  doAction: (integrationKey: string, action: string, params: any) => string;
}

export const useIntegrations = (): IUseIntegrations => {
  const [enabledIntegrations, setEnabledIntegrations] = useState<any[]>([]);
  const [integrationAccessLevel, setIntegrationAccessLevel] = useState({
    authenticated: false,
    user: null
  });
  const affiliate = useSelector(getOVSAffiliateSelector);
  const user = useSelector(getUserSelector);
  const getIntegration = (integrationKey: any): any => {
    return enabledIntegrations.find(
      (x: any) => x.integration?.title === integrationKey
    );
  };

  const INTEGRATION_ACTIONS = {
    [INTEGRATION_TYPES.INTERCOM]: {
      [INTERCOM_ACTIONS.OPEN_CHAT]: () => {
        show();
      }
    },
    [INTEGRATION_TYPES.ZOHO_DESK]: {
      [ZOHO_DESK_ACTIONS.GET_WIDGET_IFRAME]: (data: any) => {
        const { baseWidgetUrl, user } = data;
        // This will not work with emails that have a + sign in it.
        const cleanEmail = (email: string) => {
          // Find the index of the plus sign and trim the email accordingly
          const plusIndex = email.indexOf('+');
          if (plusIndex !== -1) {
            // Remove everything after the plus sign, including the plus sign
            email =
              email.substring(0, plusIndex) +
              email.substring(email.indexOf('@'));
          }
          return email;
        };

        const cleanedEmail = cleanEmail(user.email);
        const queryParams = `first_name=${user.firstName}&last_name=${user.lastName}&patient_email=${cleanedEmail}&patient_id=${user.patient?.id}&patient_phone=${user.phone}`;
        return `<iframe width="100%" height="580" src="${baseWidgetUrl}?${queryParams}" title="Brello Customer Support" frameborder="0"></iframe>`;
      }
    }
  };

  const doAction = (integrationKey: string, action: string, params: any) => {
    const actionFunction = INTEGRATION_ACTIONS[integrationKey][action];
    return actionFunction(params);
  };

  useEffect(() => {
    if (affiliate?.integrationConfigurations.length) {
      setEnabledIntegrations(affiliate?.integrationConfigurations);
    }
  }, [affiliate?.integrationConfigurations.length]);

  useEffect(() => {
    setIntegrationAccessLevel({
      authenticated: user?.id && true,
      user
    });
  }, [user?.id]);

  return {
    enabledIntegrations,
    getIntegration,
    integrationAccessLevel,
    doAction
  };
};
